import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { dehydrate } from 'react-query';

import { VirtualCategory } from '@api/catalog';
import { getVirtualCategoryDetail } from '@api/catalog/categories/api';
import { getProducts } from '@api/catalog/product/api';
import { getPagination } from '@api/common/helpers';
import { CommonResponse } from '@api/common/types';
import { getSeoTags } from '@api/seo/api';

import { fromRoubleToKopecks, getSeoFilter } from '@scripts/helpers';
import { getCommonProps } from '@scripts/server/getCommonProps';
import { ExtendedSSRDataProps } from '@scripts/server/types';

import SubCategory from '../[category]/components/SubCategory';

const VirtualCategoryDetailPage = ({ categoryId, seoData, dataProducts, total }: ExtendedSSRDataProps) => {
    return (
        <SubCategory isVirtual categoryId={categoryId} seoData={seoData} dataProducts={dataProducts} totalSSR={total} />
    );
};

const ITEMS_PER_PRODUCTS_PAGE = 24;

export const getServerSideProps = async (
    context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<ExtendedSSRDataProps>> =>
    getCommonProps(context, async (props, queryClient, apiClient) => {
        const { params } = context;

        const pageProps: ExtendedSSRDataProps = {
            isLeafCategory: true,
            categoryId: null,
            dehydratedState: null,
        };

        try {
            const urlFilters = { ...context.query };
            delete urlFilters.code;
            delete urlFilters.page;

            const parsedFilters = urlFilters?.filters ? JSON.parse(String(urlFilters?.filters)) : {};

            const price_gte = parsedFilters?.price ? fromRoubleToKopecks(parsedFilters.price[0]) : null;
            const price_lte = parsedFilters?.price ? fromRoubleToKopecks(parsedFilters.price[1]) : null;

            delete parsedFilters?.price;

            const preparedFilters = {
                ...parsedFilters,
                ...(price_gte && { price_gte }),
                ...(price_lte && { price_lte }),
            };
            const code = params?.code ? String(params.code) : String('');

            const { key: categoryVirtDetailKey, fetch: categoryVirtDetailFetch } = getVirtualCategoryDetail(apiClient)({
                filter: { code },
                include: [
                    'children',
                    'children.properties',
                    'children.property_values',
                    'properties',
                    'property_values',
                ],
            });

            const response: CommonResponse<VirtualCategory> = await queryClient.fetchQuery(
                categoryVirtDetailKey,
                categoryVirtDetailFetch
            );

            if (!response.data)
                return {
                    notFound: true,
                };

            const { key: baseProductsCategoryKey, fetch: baseProductsCategoryFetch } = getProducts(apiClient)({
                sort: 'id',
                filter: {
                    ...(response.data.id !== null && {
                        virtual_category_id: Number(response.data.id),
                    }),
                    ...preparedFilters,
                },
                pagination: getPagination(ITEMS_PER_PRODUCTS_PAGE, Number(context.query.page)),
                include: ['brand', 'category', 'images', 'attributes', 'seller'],
            });

            const dataProducts = await queryClient.fetchQuery({
                queryKey: baseProductsCategoryKey,
                queryFn: baseProductsCategoryFetch,
            });

            const { key: seoTagsKey, fetch: baseSeoTagsFetch } = getSeoTags(apiClient)(
                getSeoFilter(String(`${context.req.url}`), response.data.id)
            );

            const seoData = await queryClient.fetchQuery({
                queryKey: seoTagsKey,
                queryFn: baseSeoTagsFetch,
            });
            pageProps.dehydratedState = dehydrate(queryClient);
            pageProps.categoryId = response.data.id;

            return {
                props: {
                    dataProducts,
                    total: dataProducts?.meta?.pagination?.total || 0,
                    seoData,
                    ...pageProps,
                },
            };
        } catch (error) {
            return {
                notFound: true,
            };
        }
    });

export default VirtualCategoryDetailPage;
